import type { ThemeSeed } from "./seeds"

import { sanitizeCustomSeed } from "./custom-themes"

const SHARE_CODE_PREFIX = "TH1"
const BASE32_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"
const BASE32_LOOKUP = new Map(
  BASE32_ALPHABET.split("").map((char, index) => [char, index]),
)

type ShareMode = ThemeSeed["mode"]

export type ThemeShareCodeErrorCode =
  | "EMPTY"
  | "INVALID_PREFIX"
  | "INVALID_FORMAT"
  | "INVALID_VERSION"
  | "INVALID_CHECKSUM"
  | "INVALID_LENGTH"
  | "INVALID_PAYLOAD"

export type DecodedThemeSeedResult =
  | { ok: true; seed: ThemeSeed }
  | { ok: false; error: ThemeShareCodeErrorCode }

const H_SCALE = 10
const C_SCALE = 1000
const L_SCALE = 1000

const PAYLOAD_BYTES = 19
const CHECKSUM_BYTES = 2
const CODE_BYTES = PAYLOAD_BYTES + CHECKSUM_BYTES

function quantizeHue(value: number): number {
  return Math.round(value * H_SCALE)
}

function quantizeChroma(value: number): number {
  return Math.round(value * C_SCALE)
}

function quantizeLightness(value: number): number {
  return Math.round(value * L_SCALE)
}

function dequantizeHue(value: number): number {
  return value / H_SCALE
}

function dequantizeChroma(value: number): number {
  return value / C_SCALE
}

function dequantizeLightness(value: number): number {
  return value / L_SCALE
}

function writeUint16(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = (value >> 8) & 0xff
  bytes[offset + 1] = value & 0xff
}

function readUint16(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) << 8) | (bytes[offset + 1] ?? 0)
}

function crc16Ccitt(bytes: Uint8Array): number {
  let crc = 0xffff
  for (const byte of bytes) {
    crc ^= byte << 8
    for (let i = 0; i < 8; i++) {
      if ((crc & 0x8000) !== 0) crc = ((crc << 1) ^ 0x1021) & 0xffff
      else crc = (crc << 1) & 0xffff
    }
  }
  return crc & 0xffff
}

function encodeBase32(bytes: Uint8Array): string {
  let output = ""
  let bits = 0
  let value = 0

  for (const byte of bytes) {
    value = (value << 8) | byte
    bits += 8
    while (bits >= 5) {
      output += BASE32_ALPHABET[(value >>> (bits - 5)) & 31]
      bits -= 5
    }
  }

  if (bits > 0) {
    output += BASE32_ALPHABET[(value << (5 - bits)) & 31]
  }

  return output
}

function decodeBase32(raw: string): Uint8Array | null {
  const input = raw.trim().toUpperCase()
  if (!input) return null

  let bits = 0
  let value = 0
  const bytes: number[] = []

  for (const char of input) {
    const chunk = BASE32_LOOKUP.get(char)
    if (chunk === undefined) return null

    value = (value << 5) | chunk
    bits += 5

    if (bits >= 8) {
      bytes.push((value >>> (bits - 8)) & 0xff)
      bits -= 8
    }
  }

  if (bits > 0 && (value & ((1 << bits) - 1)) !== 0) {
    return null
  }

  return Uint8Array.from(bytes)
}

function formatChunked(code: string): string {
  return code.match(/.{1,4}/g)?.join("-") ?? code
}

function packPayload(seed: ThemeSeed): Uint8Array {
  const normalized = sanitizeCustomSeed(seed)
  const payload = new Uint8Array(PAYLOAD_BYTES)
  payload[0] = normalized.mode === "dark" ? 1 : 0

  writeUint16(payload, 1, quantizeHue(normalized.base.h))
  writeUint16(payload, 3, quantizeChroma(normalized.base.c))
  writeUint16(payload, 5, quantizeLightness(normalized.base.l))

  writeUint16(payload, 7, quantizeHue(normalized.primary.h))
  writeUint16(payload, 9, quantizeChroma(normalized.primary.c))
  writeUint16(payload, 11, quantizeLightness(normalized.primary.l))

  writeUint16(payload, 13, quantizeHue(normalized.accent.h))
  writeUint16(payload, 15, quantizeChroma(normalized.accent.c))
  writeUint16(payload, 17, quantizeLightness(normalized.accent.l))

  return payload
}

function unpackPayload(payload: Uint8Array): ThemeSeed | null {
  if (payload.length !== PAYLOAD_BYTES) return null

  const modeByte = payload[0]
  if (modeByte !== 0 && modeByte !== 1) return null

  const mode: ShareMode = modeByte === 1 ? "dark" : "light"
  const seed: ThemeSeed = {
    id: "shared-import",
    label: "Imported Theme",
    mode,
    base: {
      h: dequantizeHue(readUint16(payload, 1)),
      c: dequantizeChroma(readUint16(payload, 3)),
      l: dequantizeLightness(readUint16(payload, 5)),
    },
    primary: {
      h: dequantizeHue(readUint16(payload, 7)),
      c: dequantizeChroma(readUint16(payload, 9)),
      l: dequantizeLightness(readUint16(payload, 11)),
    },
    accent: {
      h: dequantizeHue(readUint16(payload, 13)),
      c: dequantizeChroma(readUint16(payload, 15)),
      l: dequantizeLightness(readUint16(payload, 17)),
    },
  }

  const sanitized = sanitizeCustomSeed(seed)
  return sanitized
}

function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/\s+/g, "").replace(/-/g, "")
}

export function normalizeSeedForShare(seed: ThemeSeed): ThemeSeed {
  return sanitizeCustomSeed(seed)
}

export function encodeThemeShareCode(seed: ThemeSeed): string {
  const payload = packPayload(seed)
  const checksum = crc16Ccitt(payload)
  const bytes = new Uint8Array(CODE_BYTES)

  bytes.set(payload, 0)
  writeUint16(bytes, PAYLOAD_BYTES, checksum)

  const encoded = encodeBase32(bytes)
  return `${SHARE_CODE_PREFIX}-${formatChunked(encoded)}`
}

export function decodeThemeShareCode(rawCode: string): DecodedThemeSeedResult {
  if (!rawCode.trim()) return { ok: false, error: "EMPTY" }

  const normalized = normalizeCode(rawCode)
  if (!normalized.startsWith(SHARE_CODE_PREFIX)) {
    return { ok: false, error: "INVALID_PREFIX" }
  }

  const version = normalized.slice(0, 3)
  if (version !== SHARE_CODE_PREFIX) {
    return { ok: false, error: "INVALID_VERSION" }
  }

  const payloadEncoded = normalized.slice(3)
  if (!payloadEncoded) return { ok: false, error: "INVALID_FORMAT" }

  const decoded = decodeBase32(payloadEncoded)
  if (!decoded) return { ok: false, error: "INVALID_FORMAT" }
  if (decoded.length !== CODE_BYTES) {
    return { ok: false, error: "INVALID_LENGTH" }
  }

  const payload = decoded.slice(0, PAYLOAD_BYTES)
  const checksum = readUint16(decoded, PAYLOAD_BYTES)
  const expected = crc16Ccitt(payload)
  if (checksum !== expected) {
    return { ok: false, error: "INVALID_CHECKSUM" }
  }

  const seed = unpackPayload(payload)
  if (!seed) return { ok: false, error: "INVALID_PAYLOAD" }

  return { ok: true, seed }
}
